import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import ArticleContentWrapper from './ArticleContentWrapper';
import { buildPreviewHTML } from './ArticleEditor';

/**
 * Full-article preview modal.
 * Pass either `article` (already-loaded object with .content) OR `articleId` to fetch on open.
 */
export default function ArticlePreview({ article: propArticle, articleId, onClose }) {
  const [article, setArticle] = useState(propArticle || null);
  const [loading, setLoading] = useState(!propArticle && !!articleId);
  const [error, setError]     = useState('');
  const { accessToken } = useAuth();

  useEffect(() => {
    if (propArticle) { setArticle(propArticle); return; }
    if (!articleId) return;
    setLoading(true);
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    axios.get(`/api/articles/${articleId}`, { headers })
      .then(res => setArticle(res.data))
      .catch(() => setError('Failed to load article.'))
      .finally(() => setLoading(false));
  }, [articleId, propArticle, accessToken]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '2rem 1rem' }}
      onClick={onClose}
    >
      <div
        style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '760px', width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', position: 'relative' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.75rem', borderBottom: '1px solid #e0e0e0' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Article Preview</span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '0 0.25rem' }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '1.75rem 2rem 2rem' }}>
          {loading && <p style={{ color: '#888', textAlign: 'center' }}>Loading article...</p>}
          {error   && <p style={{ color: '#dc3545' }}>{error}</p>}

          {article && (
            <>
              {/* Title */}
              <h2 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1a1a1a', lineHeight: 1.25, marginBottom: '0.75rem' }}>
                {article.title}
              </h2>

              {/* Meta line */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.82rem', color: '#888', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #f0f0f0' }}>
                {article.writer_name?.length > 0 && (
                  <span>✍️ {Array.isArray(article.writer_name) ? article.writer_name.join(', ') : article.writer_name}</span>
                )}
                {article.genre_tree?.main && (
                  <span>📂 {article.genre_tree.main}{article.genre_tree.sub ? ` › ${article.genre_tree.sub}` : ''}</span>
                )}
                {article.token_cost != null && (
                  <span>🪙 {article.token_cost} tokens</span>
                )}
                {article.date_created && (
                  <span>{new Date(article.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                )}
              </div>

              {/* Content */}
              {article.content ? (
                <>
                  <ArticleContentWrapper
                    html={buildPreviewHTML(article.content)}
                    className="article-preview-content"
                  />
                  <style>{`
                    .article-preview-content { font-size: 0.95rem; line-height: 1.75; color: #2c2c2c; word-break: break-word; }
                    .article-preview-content img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0 0.15rem; display: block; }
                    .article-preview-content figure { margin: 1rem 0; }
                    .article-preview-content figure img { margin-bottom: 0.25rem; }
                    .article-preview-content figcaption { font-size: 0.82rem; color: #666; font-style: italic; padding: 0.2rem 0.5rem; border-left: 2px solid #ddd; }
                    .article-preview-content p.img-caption { font-size: 0.82rem; color: #666; font-style: italic; margin: 0 0 0.85rem; padding: 0.25rem 0.5rem; border-left: 2px solid #ddd; }
                    .article-preview-content iframe { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; }
                    .article-preview-content a { color: #3366cc; text-decoration: underline; }
                    .article-preview-content ul, .article-preview-content ol { padding-left: 1.5rem; margin-bottom: 0.85rem; }
                    .article-preview-content h1 { font-size: 1.6rem; font-weight: 800; margin: 1rem 0 0.5rem; }
                    .article-preview-content h2 { font-size: 1.3rem; font-weight: 700; margin: 0.85rem 0 0.4rem; }
                    .article-preview-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.75rem 0 0.35rem; }
                    .article-preview-content blockquote { border-left: 3px solid #ccc; margin: 0.75rem 0; padding-left: 1rem; color: #555; font-style: italic; }
                    .article-preview-content pre { background: #f4f4f4; border-radius: 6px; padding: 0.75rem 1rem; font-family: monospace; font-size: 0.88rem; }
                  `}</style>
                  {article.wasEdited && (
                    <div style={{ marginTop: '1.75rem', paddingTop: '1rem', borderTop: '1px solid #eee', fontSize: '0.8rem', color: '#888' }}>
                      <div style={{ fontStyle: 'italic' }}>*This article has been edited.</div>
                      {article.editNote && (
                        <div style={{ marginTop: '0.3rem', color: '#666' }}>
                          <strong>Editor's note:</strong> {article.editNote}
                        </div>
                      )}
                      {article.editedAt && (
                        <div style={{ marginTop: '0.15rem', color: '#aaa', fontSize: '0.75rem' }}>
                          Last edited: {new Date(article.editedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>Purchase this article to read the full content.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
